// src/contexts/ReadingTimeContext.jsx
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

const ReadingTimeContext = createContext();

export const useReadingTime = () => {
  const context = useContext(ReadingTimeContext);
  if (!context) {
    throw new Error('useReadingTime must be used within ReadingTimeProvider');
  }
  return context;
};

export const ReadingTimeProvider = ({ children }) => {
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [displayTime, setDisplayTime] = useState('0s');
  const [currentBlogId, setCurrentBlogId] = useState(null);

  // Refs for tracking
  const startTimeRef = useRef(null);
  const activeTimeRef = useRef(0);
  const activityTimerRef = useRef(null);
  const displayTimerRef = useRef(null);
  const isPageVisibleRef = useRef(true);
  const isUserActiveRef = useRef(true);
  const lastActivityRef = useRef(Date.now());
  const hasSubmittedRef = useRef(false);
  const isActiveRef = useRef(true);
  const isInitializedRef = useRef(false);
  const blogIdRef = useRef(null);

  // Format time
  const formatDisplayTime = useCallback(seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  }, []);

  // Submit reading time
  const submitReadingTime = useCallback(async () => {
    if (hasSubmittedRef.current) return;
    if (!blogIdRef.current) return;

    // Add remaining time
    if (
      startTimeRef.current &&
      isPageVisibleRef.current &&
      isUserActiveRef.current
    ) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      activeTimeRef.current += elapsed;
    }

    const totalSeconds = Math.round(activeTimeRef.current);

    // Minimum 5 seconds to count as a reading session
    if (totalSeconds < 5) {
      console.log(`[ReadingTime] Time too short (${totalSeconds}s), skipping`);
      return;
    }

    hasSubmittedRef.current = true;
    console.log(
      `[ReadingTime] Submitting ${totalSeconds}s (${Math.round(totalSeconds / 60)} minutes) for blog ${blogIdRef.current}`,
    );

    try {
      const response = await fetch('/api/blogs?action=update-reading-time', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blogId: blogIdRef.current,
          timeSpent: totalSeconds,
        }),
      });
      const data = await response.json();
      if (data.success) {
        console.log(
          `[ReadingTime] ✅ Submitted successfully. New average: ${data.data.readingTime}min`,
        );
      } else {
        console.error('[ReadingTime] ❌ Submission failed:', data.message);
      }
    } catch (error) {
      console.error('[ReadingTime] ❌ Submission failed:', error);
    }
  }, []);

  // Start tracking for a blog
  const startTracking = useCallback(
    blogId => {
      // If already tracking this blog, don't restart
      if (blogIdRef.current === blogId && isInitializedRef.current) {
        console.log(`[ReadingTime] Already tracking blog ${blogId}`);
        return;
      }

      // If tracking a different blog, submit previous first
      if (blogIdRef.current && blogIdRef.current !== blogId) {
        submitReadingTime();
      }

      console.log(`[ReadingTime] Starting tracking for blog: ${blogId}`);
      blogIdRef.current = blogId;
      setCurrentBlogId(blogId);
      isInitializedRef.current = true;
      hasSubmittedRef.current = false;
      startTimeRef.current = Date.now();
      activeTimeRef.current = 0;
      isUserActiveRef.current = true;
      isPageVisibleRef.current = true;
      lastActivityRef.current = Date.now();
      isActiveRef.current = true;
      setIsActive(true);
      setTotalTimeSpent(0);
      setDisplayTime('0s');

      // Start display timer if not running
      if (!displayTimerRef.current) {
        displayTimerRef.current = setInterval(() => {
          if (
            startTimeRef.current &&
            isPageVisibleRef.current &&
            isUserActiveRef.current
          ) {
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            const total = Math.round(activeTimeRef.current + elapsed);
            setDisplayTime(formatDisplayTime(total));
            setTotalTimeSpent(total);
          }
        }, 1000);
      }
    },
    [submitReadingTime, formatDisplayTime],
  );

  // Stop tracking and submit
  const stopTracking = useCallback(async () => {
    if (blogIdRef.current) {
      console.log(
        `[ReadingTime] Stopping tracking for blog ${blogIdRef.current}`,
      );
      await submitReadingTime();
      // Clean up
      if (displayTimerRef.current) {
        clearInterval(displayTimerRef.current);
        displayTimerRef.current = null;
      }
      if (activityTimerRef.current) {
        clearInterval(activityTimerRef.current);
        activityTimerRef.current = null;
      }
      isInitializedRef.current = false;
      setCurrentBlogId(null);
      setIsActive(false);
    }
  }, [submitReadingTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blogIdRef.current) {
        submitReadingTime();
      }
      if (displayTimerRef.current) {
        clearInterval(displayTimerRef.current);
      }
      if (activityTimerRef.current) {
        clearInterval(activityTimerRef.current);
      }
    };
  }, [submitReadingTime]);

  return (
    <ReadingTimeContext.Provider
      value={{
        totalTimeSpent,
        isActive,
        displayTime,
        formatTime: formatDisplayTime,
        startTracking,
        stopTracking,
        currentBlogId,
      }}
    >
      {children}
    </ReadingTimeContext.Provider>
  );
};
